import { useState } from 'react'
import { Link, Navigate, Outlet } from 'react-router-dom'
import { useQuery, useMutation, gql } from 'urql'
import { useAuth } from '../context/auth-provider'
import { ConsentContext } from '../context/consent-context'
import { Logo } from './logo'
import { Button } from './ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'

const ME_QUERY = gql(`
  query MeVerified {
    me {
      emailVerified
      aiConsent
    }
  }
`)

const RESEND_MUTATION = gql(`
  mutation ResendVerificationEmailGate {
    resendVerificationEmail
  }
`)

const SET_CONSENT_MUTATION = gql(`
  mutation SetConsentWeb($type: String!, $granted: Boolean!) {
    setConsent(type: $type, granted: $granted)
  }
`)

export function ProtectedRoute() {
  const { accessToken } = useAuth()
  const [resendSent, setResendSent] = useState(false)
  const [consentPending, setConsentPending] = useState(false)
  // Local override: set when user grants consent in-app without a reload
  const [consentOverride, setConsentOverride] = useState<boolean | undefined>(undefined)

  const [{ data, fetching }] = useQuery({
    query: ME_QUERY,
    pause: !accessToken,
    requestPolicy: 'network-only',
  })

  const [, resend] = useMutation(RESEND_MUTATION)
  const [, setConsent] = useMutation(SET_CONSENT_MUTATION)

  if (!accessToken) {
    return <Navigate to="/login" replace />
  }

  if (fetching || !data) {
    return null
  }

  if (!data.me.emailVerified) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-primary/5 dark:bg-gray-900 p-4 gap-8">
        <div className="scale-150">
          <Logo />
        </div>
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Verify your email</CardTitle>
            <CardDescription>
              We sent a verification link to your inbox. Click it to start using Buylog.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <Button
              onClick={async () => { await resend({}); setResendSent(true) }}
              disabled={resendSent}
              variant="outline"
              className="w-full"
            >
              {resendSent ? 'Email sent!' : 'Resend verification email'}
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              Already verified?{' '}
              <button onClick={() => window.location.reload()} className="underline">
                Refresh to continue
              </button>
            </p>
            <div className="flex justify-center gap-4 pt-1">
              <Link to="/login" className="text-xs text-muted-foreground underline">
                Sign in with a different account
              </Link>
              <Link to="/signup" className="text-xs text-muted-foreground underline">
                Create new account
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // First-time consent gate — shown only if never answered
  if (data.me.aiConsent === null || data.me.aiConsent === undefined) {
    const handleConsent = async (granted: boolean) => {
      setConsentPending(true)
      await setConsent({ type: 'ai_features_v1', granted })
      setConsentPending(false)
      window.location.reload()
    }

    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-primary/5 dark:bg-gray-900 p-4 gap-8">
        <div className="scale-150">
          <Logo />
        </div>
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Your smart shopping assistant</CardTitle>
            <CardDescription>
              Buylog uses AI to fill in receipts automatically and surface savings in your spending.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <Button
              onClick={() => handleConsent(true)}
              disabled={consentPending}
              className="w-full"
            >
              Enable AI Features
            </Button>
            <Button
              onClick={() => handleConsent(false)}
              disabled={consentPending}
              variant="outline"
              className="w-full"
            >
              No Thanks
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              Your data is used only to generate your results and is never stored. Powered by Claude (Anthropic).
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  // aiConsent is resolved — provide it to all child routes.
  // consentOverride lets children grant consent in-place without a page reload.
  const resolvedConsent = consentOverride ?? data.me.aiConsent

  const grantConsent = async () => {
    await setConsent({ type: 'ai_features_v1', granted: true })
    setConsentOverride(true)
  }

  return (
    <ConsentContext.Provider value={{ aiConsent: resolvedConsent, grantConsent }}>
      <Outlet />
    </ConsentContext.Provider>
  )
}
